import {format} from 'date-fns';
import React from 'react';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';

import {FlexRow, Icon, Text} from '@forecast-it/design-system';

import {getWorkingHoursForPersonForDate} from '../../../../../util/time-registration/working-hours/WorkingHoursUtil';
import Util from '../../../../../util/util';

import type {TimeRegistrationModalTimeOnDay_viewer} from './__generated__/TimeRegistrationModalTimeOnDay_viewer.graphql';

type WorkingHoursForDayFromViewerInput = {viewer: TimeRegistrationModalTimeOnDay_viewer; date: Date};
function workingHoursForDayFromViewer({viewer, date}: WorkingHoursForDayFromViewerInput) {
	const person = viewer.company?.person;
	if (!person) return 0;

	const personStartDate = person.startDate
		? new Date(person.startDate)
		: person.createdAt
		? new Date(person.createdAt)
		: null;
	if (!personStartDate) return 0;

	const personEndDate = person.endDate ? new Date(person.endDate) : null;
	const workingHoursByDay = {
		sunday: person.sunday || 0,
		monday: person.monday || 0,
		tuesday: person.tuesday || 0,
		wednesday: person.wednesday || 0,
		thursday: person.thursday || 0,
		friday: person.friday || 0,
		saturday: person.saturday || 0,
	};

	return getWorkingHoursForPersonForDate({date, workingHoursByDay, personStartDate, personEndDate});
}

export function TimeRegistrationModalTimeOnDayPlaceholder(): JSX.Element {
	return (
		<FlexRow gap="xl">
			<Text>{`-- out of -- registered on --/--/----`}</Text>
		</FlexRow>
	);
}

type TimeRegistrationModalTimeOnDayProps = {
	viewer: TimeRegistrationModalTimeOnDay_viewer;
	date: Date;
};
function TimeRegistrationModalTimeOnDay({viewer, date}: TimeRegistrationModalTimeOnDayProps): JSX.Element {
	const workingHoursForTheDay = workingHoursForDayFromViewer({viewer, date});
	const intl = useIntl();
	const timeOnDay = viewer.company?.person?.timeRegistrations?.edges?.reduce(
		(acc, edge) => acc + (edge?.node?.minutesRegistered || 0),
		0
	);

	const formattedTimeOnDay = Util.convertMinutesToFullHour(timeOnDay, intl, true);
	const formattedWorkingHoursForTheDay = Util.convertMinutesToFullHour(workingHoursForTheDay, intl, true);
	const formattedDate = format(date, 'P');

	return (
		<FlexRow gap={'xxs'}>
			<Text color="medium">{`${formattedTimeOnDay} out of ${formattedWorkingHoursForTheDay} registered on ${formattedDate}`}</Text>
			{timeOnDay === workingHoursForTheDay && <Icon icon="tick" size="l" />}
		</FlexRow>
	);
}

export const TimeRegistrationModalTimeOnDayQuery = graphql`
	query TimeRegistrationModalTimeOnDay_Query($personId: ID, $date: String) {
		viewer {
			actualPersonId
			component(name: "time_registration_modal_time_on_day")
			...TimeRegistrationModalTimeOnDay_viewer @arguments(personId: $personId, date: $date)
		}
	}
`;

export default createFragmentContainer(TimeRegistrationModalTimeOnDay, {
	viewer: graphql`
		fragment TimeRegistrationModalTimeOnDay_viewer on Viewer
		@argumentDefinitions(personId: {type: "ID!"}, date: {type: "String"}) {
			company {
				person(id: $personId) {
					startDate
					endDate
					createdAt
					monday
					tuesday
					wednesday
					thursday
					friday
					saturday
					sunday
					timeRegistrations(startDate: $date, endDate: $date) {
						edges {
							node {
								minutesRegistered
							}
						}
					}
				}
			}
		}
	`,
});
